import { hashKey, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import type { ThemeView } from '@tallyframe/core';
import { queryKeys } from '../api/queries';
import { useBrandQuery, useThemeQuery } from '../api/settings-queries';
import type { BrandState } from '../brandContext';

export type Session = 'unknown' | 'signed-in' | 'signed-out';

/** The cache key for the brand and theme before a workspace is known (admin mode). */
const ADMIN_KEY = '(principal)';

type Kind = 'brand' | 'theme';

/**
 * On the move out of `'signed-in'`, drops every cached `kind` entry, for every
 * workspace. Removing a query also cancels its in-flight load, so a load that
 * was running at sign-out can never land, and the next sign-in on this tab
 * starts from nothing instead of the previous operator's cached value.
 */
function useDropOnSignOut(client: QueryClient, kind: Kind, signedIn: boolean): void {
  const wasSignedIn = useRef(signedIn);
  useEffect(() => {
    if (wasSignedIn.current && !signedIn) {
      client.removeQueries({
        predicate: (query) =>
          query.queryKey.length === 3 && query.queryKey[0] === 'workspaces' && query.queryKey[2] === kind
      });
    }
    wasSignedIn.current = signedIn;
  }, [client, kind, signedIn]);
}

/**
 * The operator's brand for the current workspace, read through
 * `useBrandQuery`. Loads once `session` becomes `'signed-in'`, and reads as
 * `null` in the very render the session stops being `'signed-in'` (sign-out
 * or a 401 bounce): the value is derived from the session, not stored, so
 * nothing has to resolve before it clears.
 *
 * `reloadBrand` cancels any load still in flight (aborting its request) before
 * starting a fresh one, so only the newest call's result is ever applied,
 * whatever order the responses arrive in.
 */
export function useBrandState(
  session: Session,
  workspaceId: string | null = null
): Pick<BrandState, 'brand' | 'reloadBrand'> {
  const key = workspaceId ?? ADMIN_KEY;
  const signedIn = session === 'signed-in';
  const client = useQueryClient();
  const query = useBrandQuery(key, signedIn);
  useDropOnSignOut(client, 'brand', signedIn);

  // The committed key: updated at commit, never during a render that may be discarded.
  const keyRef = useRef(key);
  useLayoutEffect(() => {
    keyRef.current = key;
  }, [key]);
  const reloadBrand = useCallback(async () => {
    const queryKey = queryKeys.brand(keyRef.current);
    await client.cancelQueries({ queryKey, exact: true });
    await client.refetchQueries({ queryKey, exact: true });
  }, [client]);

  // Not fatal on failure: the top bar just shows no operator name or logo.
  const brand = signedIn && query.status !== 'error' ? (query.data ?? null) : null;
  return useMemo(() => ({ brand, reloadBrand }), [brand, reloadBrand]);
}

type ThemeState = Pick<BrandState, 'themeView' | 'beginThemeSave' | 'applySavedTheme'>;

/**
 * The workspace theme, read through `useThemeQuery` with the brand's
 * lifecycle: keyed by workspace, so a switch shows only the new workspace's
 * theme, and `null` in the same render as sign-out.
 *
 * `beginThemeSave`/`applySavedTheme` let a settings save apply its PUT
 * response only if nothing (a workspace switch, a sign-out, a theme load) has
 * bumped the token since, so a slow save cannot show one workspace's theme in
 * another. An applied save cancels any load still in flight, so that load
 * cannot overwrite it when it lands, and shows it in the same render.
 */
export function useThemeState(session: Session, workspaceId: string | null = null): ThemeState {
  const key = workspaceId ?? ADMIN_KEY;
  const signedIn = session === 'signed-in';
  const client = useQueryClient();
  const query = useThemeQuery(key, signedIn);
  useDropOnSignOut(client, 'theme', signedIn);

  // Holds the committed key and session. Bumped in a layout effect, which runs
  // at commit and before any handler or promise callback can call
  // `applySavedTheme`, so a token taken before a switch or sign-out is stale
  // from the render that shows the change; a discarded render bumps nothing.
  const tokenRef = useRef({ key, signedIn, n: 0 });
  useLayoutEffect(() => {
    const current = tokenRef.current;
    if (current.key !== key || current.signedIn !== signedIn) {
      tokenRef.current = { key, signedIn, n: current.n + 1 };
    }
  }, [key, signedIn]);

  // A theme request that starts a new fetch bumps the token too. A refetch that
  // replaces a fetch already in flight (cancelRefetch) dispatches no new fetch
  // and does not bump it.
  useEffect(() => {
    const hash = hashKey(queryKeys.theme(key));
    return client.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'fetch' && event.query.queryHash === hash) {
        tokenRef.current.n += 1;
      }
    });
  }, [client, key]);

  // The query cache tells its observers on a later tick; a render now reads the cache directly.
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const beginThemeSave = useCallback(() => tokenRef.current.n, []);
  const applySavedTheme = useCallback(
    (result: ThemeView, token: number) => {
      if (tokenRef.current.n !== token) return;
      // Makes a repeat of this token a no-op.
      tokenRef.current.n += 1;
      const queryKey = queryKeys.theme(tokenRef.current.key);
      void client.cancelQueries({ queryKey, exact: true });
      client.setQueryData<ThemeView>(queryKey, { theme: result.theme, entitled: result.entitled });
      rerender();
    },
    [client]
  );

  // Not fatal on failure: reports render with the default font.
  const themeView = signedIn && query.status !== 'error' ? (query.data ?? null) : null;
  return useMemo(
    () => ({ themeView, beginThemeSave, applySavedTheme }),
    [themeView, beginThemeSave, applySavedTheme]
  );
}

/** The brand and theme together, as the one value `BrandContext` carries. */
export function useBrandContextState(session: Session, workspaceId: string | null): BrandState {
  const brandState = useBrandState(session, workspaceId);
  const { themeView, beginThemeSave, applySavedTheme } = useThemeState(session, workspaceId);
  return useMemo(
    () => ({ ...brandState, themeView, beginThemeSave, applySavedTheme }),
    [brandState, themeView, beginThemeSave, applySavedTheme]
  );
}
